import { useEffect, useRef, useState, type ChangeEvent } from 'react'
import { createPortal } from 'react-dom'
import { Download, FastForward, Focus, Maximize2, Minimize2 } from 'lucide-react'
import { NodeToolbar, type Position } from '@xyflow/react'
import type { ReactNode } from 'react'
import { toast } from 'sonner'
import { cn, matchesAccept } from '@/shared/lib/utils'
import { validateFiles } from '@/shared/lib/utils/file-validation'
import { modLabel } from '@/shared/lib/utils/platform-utils'
import { downloadRemoteFile } from "@/shared/lib/utils/download-utils.ts";
import { useNodeAwareness } from '@/entities/node/lib/node-awareness'
import { useNodeCentering } from '@/entities/node/lib/useNodeCentering'
import { useCustomNodeData } from "@/entities/node";
import { Tip } from '@/shared/components/ui/tooltip'
import type { BaseNodeData } from '@/shared/lib/node-registry'
import { MATERIAL_OP } from '@/shared/lib/op-registry'
import { useNodeToolbarHostContext } from '@/shared/lib/node-toolbar-context'

export function NodeToolbarFrame({
  children,
  hidden = false,
  offset = 20,
  position,
  nodeId,
}: {
  children: ReactNode
  hidden?: boolean
  offset?: number
  position: Position
  nodeId?: string
}) {
  const resolvedNodeId = nodeId ?? ''
  const nodeState = useNodeAwareness(resolvedNodeId)
  const isUploading = !!nodeState.isUploading

  if (hidden || isUploading) {
    return null
  }

  return (
    <NodeToolbar
      nodeId={resolvedNodeId}
      isVisible
      position={position}
      align="center"
      className="select-none"
      offset={offset}
    >
      {children}
    </NodeToolbar>
  )
}

export function ToolbarStripShell({
  children,
  className,
}: {
  children: ReactNode
  className?: string
}) {
  const { isExiting } = useNodeToolbarHostContext()
  return (
    <div
      className={cn(
        'nopan inline-flex h-8 max-w-full items-center justify-center gap-1 rounded-full border border-[#3c3c3c] bg-[#252525E6] px-3 text-xs text-[#d0d0d0] shadow-[0_18px_48px_rgba(0,0,0,0.42)] outline-1 outline-white/6',
        isExiting ? 'animate-toolbar-exit' : 'animate-toolbar-enter',
        className,
      )}
    >
      {children}
    </div>
  )
}

export function EditorPanelShell({
  children,
  widthClass = 'w-[640px] max-w-[calc(100vw-96px)]',
  allowMaximize = false,
  isMaximized: controlledIsMaximized,
  onMaximizedChange,
  className,
}: {
  children: ReactNode
  widthClass?: string
  allowMaximize?: boolean
  isMaximized?: boolean
  onMaximizedChange?: (maximized: boolean) => void
  className?: string
}) {
  const [internalIsMaximized, setInternalIsMaximized] = useState(false)
  const isMaximized = controlledIsMaximized ?? internalIsMaximized
  const { isExiting } = useNodeToolbarHostContext()

  const setIsMaximized = (val: boolean) => {
    setInternalIsMaximized(val)
    if (onMaximizedChange) onMaximizedChange(val)
  }

  useEffect(() => {
    if (!isMaximized) return
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        setIsMaximized(false)
      }
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [isMaximized])

  const content = (
    <div
      className={cn(
        isMaximized ? 'is-maximized group/panel w-[800px] max-w-[90vw] h-[800px] max-h-[90vh] shadow-[0_24px_64px_rgba(0,0,0,0.6)] flex flex-col [&>div:first-of-type]:flex-1 [&>div:first-of-type]:flex [&>div:first-of-type]:flex-col [&>div:first-of-type]:min-h-0 [&>div:first-of-type>*:not(.prompt-textarea-wrapper)]:shrink-0' : widthClass,
        'nopan relative rounded-2xl border border-[#404040] bg-[rgba(31,31,31,0.9)] px-3 py-2 text-white backdrop-blur-[5px]',
        isExiting ? 'animate-op-exit' : 'animate-op-enter',
        className,
      )}
    >
      {allowMaximize && (
        <Tip content={isMaximized ? '还原' : '最大化'} delayDuration={0}>
          <button
            type="button"
            onClick={() => setIsMaximized(!isMaximized)}
            className="nodrag absolute right-2 top-2 z-50 flex h-6 w-6 items-center justify-center rounded-md text-[#A4A4A4] transition hover:bg-white/10 hover:text-white"
          >
            {isMaximized ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
          </button>
        </Tip>
      )}
      {children}
    </div>
  )

  if (isMaximized) {
    return createPortal(
      <div
        className="fixed inset-0 z-49 flex items-center justify-center bg-black/60 backdrop-blur-sm"
        onPointerDown={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
        onClick={(e) => {
          e.stopPropagation()
        }}
      >
        {content}
      </div>,
      document.body
    )
  }

  return content
}

export function ToolbarIconButton({
  children,
  className,
  disabled = false,
  isSelected = false,
  onClick,
  title,
  showTitle,
  tooltip,
}: {
  children: ReactNode
  className?: string
  disabled?: boolean
  isSelected?: boolean
  onClick?: () => void
  title?: string
  showTitle?: boolean
  tooltip?: string
}) {
  const button = (
    <button
      type="button"
      onPointerDown={(event) => {
        if (disabled) {
          return
        }

        event.preventDefault()
      }}
      onClick={onClick}
      disabled={disabled}
      className={cn(
        'nodrag flex h-6 w-auto items-center justify-center gap-1 rounded-full border border-transparent px-2 text-xs text-[#d0d0d0] transition hover:bg-[#3d3d3d] hover:text-white',
        disabled && 'cursor-not-allowed opacity-40 hover:bg-transparent hover:text-white/70',
        isSelected && 'bg-[#3d3d3d] text-white',
        className,
      )}
    >
      {children}
      {showTitle ? <span>{title}</span> : null}
    </button>
  )

  const tooltipText = tooltip ?? title

  if (tooltipText) {
    return (
      <Tip content={tooltipText} delayDuration={0}>
        {button}
      </Tip>
    )
  }

  return button
}

/**
 * 文件上传 hook — 从 ToolbarUploadShell 提取的通用上传逻辑。
 *
 * 封装隐藏 file input 的引用、上传状态管理、文件类型校验和错误处理。
 * 可用于任何需要触发文件选择的场景（如角标按钮），
 * 调用方自行渲染触发按钮并将 inputRef 挂载到隐藏 <input> 上。
 */
export function useFileUpload({
  accept,
  onFileSelect,
  onFilesSelect,
  onSelectError,
  invalidTypeMessage = '所选文件格式不受支持',
}: {
  accept: string
  onFileSelect?: (file: File) => void | Promise<void>
  onFilesSelect?: (files: File[]) => void | Promise<void>
  onSelectError?: (message: string, file?: File) => void
  invalidTypeMessage?: string
}) {
  const inputRef = useRef<HTMLInputElement | null>(null)
  const [isUploading, setIsUploading] = useState(false)

  function emitError(message: string, file?: File) {
    if (onSelectError) {
      onSelectError(message, file)
      return
    }

    toast.error(message)
  }

  function trigger() {
    if (isUploading) return
    if (inputRef.current) {
      inputRef.current.value = ''
      inputRef.current.click()
    }
  }

  async function handleFileSelect(event: ChangeEvent<HTMLInputElement>) {
    const inputElement = event.currentTarget
    const files = Array.from(inputElement.files ?? [])
    if (files.length === 0) return

    for (const file of files) {
      if (!matchesAccept(file, accept)) {
        emitError(invalidTypeMessage, file)
        inputElement.value = ''
        return
      }
    }

    setIsUploading(true)
    try {
      const validFiles = await validateFiles(files)
      if (validFiles.length === 0) return

      if (onFilesSelect) {
        await onFilesSelect(validFiles)
      } else if (onFileSelect) {
        await Promise.all(validFiles.map(file => onFileSelect(file)))
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : '文件处理失败'
      emitError(message, files[0])
    } finally {
      setIsUploading(false)
      inputElement.value = ''
    }
  }

  return {
    inputRef,
    isUploading,
    trigger,
    handleFileSelect,
  }
}

export function ToolbarUploadShell({
  accept,
  multiple = false,
  children,
  className,
  disabled = false,
  invalidTypeMessage = '所选文件格式不受支持',
  onFileSelect,
  onFilesSelect,
  onSelectError,
  showTitle = true,
  title = '上传',
  uploadingTitle = '上传中',
  nodeId,
}: {
  accept: string
  multiple?: boolean
  children: ReactNode
  className?: string
  disabled?: boolean
  invalidTypeMessage?: string
  onFileSelect?: (file: File) => void | Promise<void>
  onFilesSelect?: (files: File[]) => void | Promise<void>
  onSelectError?: (message: string, file?: File) => void
  showTitle?: boolean
  title?: string
  uploadingTitle?: string
  nodeId?: string
}) {
  const resolvedNodeId = nodeId ?? ''
  const { inputRef, isUploading, trigger, handleFileSelect } = useFileUpload({
    accept,
    onFileSelect,
    onFilesSelect,
    onSelectError,
    invalidTypeMessage,
  })
  const resolvedDisabled = disabled || isUploading
  const { snapshot } = useCustomNodeData<BaseNodeData>(resolvedNodeId)
  const isMaterialNode = snapshot.op === MATERIAL_OP
  const hasAsset = Array.isArray(snapshot.assetRefs) && snapshot.assetRefs.length > 0

  if (!isMaterialNode && hasAsset) return null
  if (isMaterialNode) {
    title = "替换"
  }

  return (
    <>
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        multiple={multiple}
        className="hidden"
        onChange={handleFileSelect}
      />
      <ToolbarIconButton
        onClick={trigger}
        disabled={resolvedDisabled}
        title={isUploading ? uploadingTitle : title}
        showTitle={showTitle}
        className={className}
      >
        {children}
      </ToolbarIconButton>
    </>
  )
}

export function ToolbarDownloadShell({
  className,
  disabled = false,
  fallbackBaseName = 'download',
  showTitle = false,
  title = '下载',
  url,
  onDownload,
}: {
  className?: string
  disabled?: boolean
  fallbackBaseName?: string
  showTitle?: boolean
  title?: string
  url?: string | null
  onDownload?: () => void | Promise<void>
}) {
  const [isDownloading, setIsDownloading] = useState(false)
  const resolvedDisabled = disabled || isDownloading || (!url && !onDownload)

  async function handleClick() {
    if (resolvedDisabled) {
      return
    }

    setIsDownloading(true)
    try {
      if (onDownload) {
        await onDownload()
      } else if (url) {
        await downloadRemoteFile(url, fallbackBaseName)
      }
    } catch (error) {
      toast.error(error instanceof Error ? error.message : '下载失败')
    } finally {
      setIsDownloading(false)
    }
  }

  return (
    <ToolbarIconButton
      onClick={() => {
        void handleClick()
      }}
      disabled={resolvedDisabled}
      title={title}
      showTitle={showTitle}
      className={className}
    >
      <Download className="h-4 w-4" />
    </ToolbarIconButton>
  )
}

export function ToolbarActionDivider() {
  return <span className="mx-1 h-5 w-px bg-white/10" />
}

export function ToolbarZoomButton({ nodeId }: { nodeId: string }) {
  const { centerNodeById, centeredNodeId, restorePreviousViewport } = useNodeCentering()
  const isZoomed = centeredNodeId === nodeId

  return (
    <ToolbarIconButton
      onClick={() => {
        if (isZoomed) {
          restorePreviousViewport()
        } else {
          centerNodeById(nodeId, { duration: 400 })
        }
      }}
      title={isZoomed ? "取消聚焦 (F)" : "聚焦 (F)"}
    >
      {isZoomed ? <Minimize2 className="h-4 w-4" /> : <Focus className="h-4 w-4" />}
    </ToolbarIconButton>
  )
}

export function ToolbarPreviewButton({
  onPreview
}: {
  onPreview: () => void
}) {
  return (
    <ToolbarIconButton
      onClick={onPreview}
      title="预览 (A)"
    >
      <Maximize2 className="h-4 w-4" />
    </ToolbarIconButton>
  )
}

export function ToolbarFastForwardButton({
  onFastForward
}: {
  onFastForward: () => void
}) {
  return (
    <ToolbarIconButton
      onClick={onFastForward}
      title={`快速执行 (${modLabel()}+Enter)`}
    >
      <FastForward className="h-4 w-4" />
    </ToolbarIconButton>
  )
}
